import { useEffect, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'

import { loginPath } from '@/components/SignIn/nextPath'
import { authStore } from '@/stores/authStore'
import { Button } from '@/UI-KIT/Button/Button'
import { Typography } from '@/UI-KIT/Typography/Typography'

import styles from './RequireAuth.module.scss'

/**
 * Закрытый экран: кабинет всегда, мастер — при коде сразу после почты (utils/authGate.ts). Страница
 * «спасибо» не закрыта — см. AuthKnown ниже. Пока неизвестно, вошёл ли человек, не рисуется
 * ничего — ни экран, ни вход: иначе вошедший видел бы вспышку экрана входа на каждой загрузке.
 * Не вошёл — на вход, с возвратом сюда же (адрес и query целиком: страница «спасибо» без своего
 * query не знает, что куплено).
 *
 * Экран рисуется только ПОСЛЕ того, как копия кабинета в браузере заполнена с сервера: сторы экранов
 * читают её в конструкторе.
 */
export const RequireAuth = observer(({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const location = useLocation()

  useEffect(() => {
    authStore.init()
  }, [])

  if (authStore.isAuthenticated) return <>{children}</>
  if (authStore.status === 'anonymous') {
    return <Navigate to={loginPath(`${location.pathname}${location.search}`)} replace />
  }
  if (authStore.unavailable) {
    return (
      <div className={styles.root}>
        <Typography variant="t2" color="ink2">
          {t('auth.unavailable')}
        </Typography>
        <Button colorScheme="blue" onClick={authStore.retry}>
          {t('auth.retry')}
        </Button>
      </div>
    )
  }
  return null
})

/**
 * Открытый экран, которому важно, вошёл ли человек, — страница «спасибо». Она не закрыта входом:
 * оплативший должен получить тариф и Purchase сразу, а не после кода. Но вошедшему заказ сверяется
 * с сервером (второе устройство не должно отправить Purchase дважды), поэтому экран ждёт ответа «вошёл
 * ли», прежде чем смонтироваться. Без cookie ответ мгновенный; монолит молчит — экран рисуется как для
 * анонима: потерять покупку хуже, чем не сверить её.
 */
export const AuthKnown = observer(({ children }: { children: ReactNode }) => {
  useEffect(() => {
    authStore.init()
  }, [])

  if (authStore.status === 'unknown' && !authStore.unavailable) return null
  return <>{children}</>
})
